import React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import Close from "@material-ui/icons/Close";
import FolderOpen from "@material-ui/icons/FolderOpen";
import FarApi from "../../../../assets/js/utils/far/farApi";
import {
    FAR_OP, FAR_EXCLUSION, FAR_EXCLUSION_LABEL, FAR_MAX_BLOCKED_ENTRIES,
    normalizeBlockedEmail, normalizeBlockedDomain, describeBlockedDomainError, emailDomain
} from "../../../../assets/js/utils/far/farEngine";
import farDialogStyle from "../../../../assets/jss/user/far/farDialogStyle.jsx";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";
import FarEmailChips from "./FarEmailChips";
import { FarNotice } from "./farUi";
import LoaderComponent from "../../Loader";
import MyDocsPickerModal from "../MyDocs/MyDocsPickerModal";

const useStyles = makeStyles(theme => {
    const base = farDialogStyle(theme);
    const t = farShared(theme).t;
    return Object.assign(base, {
        exclusionSection: { padding: "14px", borderRadius: "10px", border: "1px solid "+t.danger.border, backgroundColor: t.dark ? "rgba(239,68,68,0.05)" : "#FFFBFB" },
        blockedChip: { backgroundColor: t.danger.bg, borderColor: t.danger.border, color: t.danger.fg },
        radioGroup: { display: "flex", flexDirection: "column", gap: "8px", border: "none", margin: 0, padding: 0, minWidth: 0 },
        legend: { ...base.label, padding: 0, marginBottom: "6px" },
        radio: {
            display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "14px", cursor: "pointer", color: t.text, lineHeight: "20px",
            "& input": { width: "18px", height: "18px", accentColor: t.danger.solid, cursor: "pointer", margin: "1px 0 0", flexShrink: 0 },
            "& small": { display: "block", fontSize: "12px", color: t.textMuted, lineHeight: "17px" }
        },
        example: { margin: 0, padding: "10px 12px", borderRadius: "8px", backgroundColor: t.surfaceAlt, border: "1px solid "+t.border, fontSize: "13px", lineHeight: "19px", color: t.text, "& strong": { color: t.textStrong } }
    });
});

const EXCLUSION_HELP = {
    [FAR_EXCLUSION.BLOCK]: "The recipient must be removed. No one can override it.",
    [FAR_EXCLUSION.ADMIN]: "Only an administrator can authorize the send, with a reason. It is recorded in the audit log.",
    [FAR_EXCLUSION.GLOBAL]: "The normal exception options from FAR Settings apply."
};

/** Authorized entries that a blocked email or domain overrides (shown as a warning). */
const findConflicts = (authorized, blockedEmails, blockedDomains) => {
    const conflicts = [];
    (authorized || []).forEach(entry => {
        const value = String(entry || "").toLowerCase();
        const isDomainEntry = value.indexOf("@") === 0 || value.indexOf("*@") === 0;
        const domain = isDomainEntry ? value.substring(value.indexOf("@") + 1) : emailDomain(value);
        if((!isDomainEntry && blockedEmails.includes(value)) || (domain && blockedDomains.includes(domain))){
            conflicts.push(entry);
        }
    });
    return conflicts;
};

const emptyRule = () => ({
    id: null, name: "", description: "", active: true,
    prefix: "", prefixOp: FAR_OP.AND, suffix: "", suffixOp: FAR_OP.AND, directory: "", sendlinxFolder: "",
    emails: [], blockedEmails: [], blockedDomains: [], exclusionAction: FAR_EXCLUSION.BLOCK, blockedListsVisible: true
});

const opValue = (op) => (op === FAR_OP.OR ? FAR_OP.OR : FAR_OP.AND);

function FarRuleModal({ open, rule, mode, onClose, onSaved }){
    const classes = useStyles();
    const [form, setForm] = React.useState(() => Object.assign(emptyRule(), rule || {}, {
        prefixOp: opValue(rule ? rule.prefixOp : FAR_OP.AND),
        suffixOp: opValue(rule ? rule.suffixOp : FAR_OP.AND)
    }));
    const [errors, setErrors] = React.useState({});
    const [saving, setSaving] = React.useState(false);
    const [serverError, setServerError] = React.useState("");
    const [folderPicker, setFolderPicker] = React.useState(false);
    const sourceRef = React.useRef(null);

    React.useEffect(() => () => {
        if(sourceRef.current){ sourceRef.current.cancel("Request Cancelled"); }
    }, []);

    const set = (name, value) => {
        setForm(prev => Object.assign({}, prev, { [name]: value }));
        if(errors[name] || (["prefix", "suffix", "directory", "sendlinxFolder"].includes(name) && errors.conditions)){
            setErrors(prev => Object.assign({}, prev, { [name]: null, conditions: null }));
        }
    };

    const validate = () => {
        const next = {};
        if(form.name.trim().length <= 0){
            next.name = "Rule name is required.";
        }
        if(!form.prefix.trim() && !form.suffix.trim() && !form.directory.trim() && !(form.sendlinxFolder || "").trim()){
            next.conditions = "Add at least one condition: prefix, suffix, Sendlinx folder or local folder.";
        }
        if(form.emails.length <= 0){
            next.emails = "Add at least one authorized recipient.";
        }
        setErrors(next);
        return Object.keys(next).length <= 0;
    };

    const save = () => {
        if(saving || !validate()){
            return;
        }
        const hasFolder = !!(form.directory.trim() || (form.sendlinxFolder || "").trim());
        const source = axios.CancelToken.source();
        sourceRef.current = source;
        setSaving(true);
        setServerError("");
        const payload = Object.assign({}, form, {
            id: mode === "duplicate" ? null : form.id,
            blockedEmails: form.blockedEmails || [],
            blockedDomains: form.blockedDomains || [],
            exclusionAction: form.exclusionAction || FAR_EXCLUSION.BLOCK,
            name: form.name.trim(),
            description: form.description.trim(),
            prefix: form.prefix.trim(),
            suffix: form.suffix.trim(),
            directory: form.directory.trim(),
            sendlinxFolder: (form.sendlinxFolder || "").trim(),
            // Operators only matter between two present conditions; store None otherwise (legacy Outlook value).
            // Both folder conditions sit in the same slot after the suffix (web: Sendlinx folder, Outlook: local folder).
            prefixOp: form.prefix.trim() && (form.suffix.trim() || hasFolder) ? form.prefixOp : FAR_OP.NONE,
            suffixOp: form.suffix.trim() && hasFolder ? form.suffixOp : FAR_OP.NONE
        });
        FarApi.saveRule(payload, source).then(saved => {
            setSaving(false);
            onSaved(saved);
        }).catch(err => {
            if(err && err.message === "Request Cancelled"){
                return;
            }
            setSaving(false);
            let message = "The rule could not be saved. Please try again.";
            if(err && typeof(err.message) === "string"){
                message = err.message;
            }else if(err && typeof(err) === "object"){
                const values = Object.values(err).filter(v => typeof(v) === "string");
                if(values.length > 0){ message = values.join(" "); }
            }
            setServerError(message);
        });
    };

    const hasPrefix = form.prefix.trim().length > 0;
    const hasSuffix = form.suffix.trim().length > 0;
    const hasDirectory = form.directory.trim().length > 0;
    const hasSendlinxFolder = (form.sendlinxFolder || "").trim().length > 0;
    const showPrefixOp = hasPrefix && (hasSuffix || hasDirectory || hasSendlinxFolder);
    const showSuffixOp = hasSuffix && (hasDirectory || hasSendlinxFolder);
    const title = mode === "edit" ? "Edit Rule" : (mode === "duplicate" ? "Duplicate Rule" : "Add Rule");
    const canEditExclusions = mode !== "edit" || form.blockedListsVisible !== false;
    const blockedEmails = form.blockedEmails || [];
    const blockedDomains = form.blockedDomains || [];
    const hasExclusions = blockedEmails.length + blockedDomains.length > 0;
    const conflicts = findConflicts(form.emails, blockedEmails, blockedDomains);
    const exampleRecipient = blockedEmails.length > 0 ? blockedEmails[0] : (blockedDomains.length > 0 ? "sales@"+blockedDomains[0] : "");
    const exampleFile = (form.prefix.trim() ? form.prefix.trim()+"_example" : "example") + (form.suffix.trim() || ".pdf");

    const opSelect = (name, label) => (
        <div className={classes.field}>
            <label className={classes.label} htmlFor={"far-rule-"+name}>{label}</label>
            <select id={"far-rule-"+name} className={classes.select} value={form[name]} onChange={(e) => set(name, parseInt(e.target.value, 10))}>
                <option value={FAR_OP.AND}>AND</option>
                <option value={FAR_OP.OR}>OR</option>
            </select>
        </div>
    );

    return (
        <Dialog open={open} onClose={() => (saving ? null : onClose())} classes={{ paper: classes.paper }} aria-labelledby="far-rule-title" scroll="body">
            <div className={classes.header}>
                <div>
                    <h2 className={classes.title} id="far-rule-title">{title}</h2>
                    <p className={classes.subtitle}>Files matching these conditions can only be sent to the authorized recipients.</p>
                </div>
                <button type="button" className={classes.close} aria-label="Close" onClick={onClose} disabled={saving}><Close /></button>
            </div>
            <form className={classes.body} onSubmit={(e) => { e.preventDefault(); save(); }} noValidate>
                { serverError ? <FarNotice tone="danger">{serverError}</FarNotice> : null }

                <section className={classes.section} aria-labelledby="far-rule-identity">
                    <h3 className={classes.sectionTitle} id="far-rule-identity">Rule identity</h3>
                    <div className={classes.field}>
                        <label className={classes.label} htmlFor="far-rule-name">Rule name<span className={classes.required} aria-hidden="true">*</span></label>
                        <input id="far-rule-name" className={classes.input+" "+(errors.name ? classes.inputError : "")} value={form.name}
                            onChange={(e) => set("name", e.target.value)} placeholder="e.g. ABC Inc Invoices" maxLength={120}
                            aria-required="true" aria-invalid={errors.name ? "true" : "false"} aria-describedby={errors.name ? "far-rule-name-error" : undefined} autoFocus />
                        { errors.name ? <p className={classes.error} id="far-rule-name-error" role="alert">{errors.name}</p> : null }
                    </div>
                    <div className={classes.field}>
                        <label className={classes.label} htmlFor="far-rule-description">Description</label>
                        <input id="far-rule-description" className={classes.input} value={form.description}
                            onChange={(e) => set("description", e.target.value)} placeholder="e.g. Customer invoices to AP" maxLength={255} />
                    </div>
                    <div className={classes.switchRow}>
                        <button type="button" role="switch" aria-checked={form.active ? "true" : "false"} aria-labelledby="far-rule-active-label"
                            className={classes.toggle} onClick={() => set("active", !form.active)} />
                        <span id="far-rule-active-label">{form.active ? "Active — checked when sending" : "Inactive — not checked"}</span>
                    </div>
                </section>

                <section className={classes.section} aria-labelledby="far-rule-conditions" aria-describedby="far-rule-conditions-help">
                    <h3 className={classes.sectionTitle} id="far-rule-conditions">File conditions</h3>
                    <div className={classes.row}>
                        <div className={classes.field+" "+classes.grow}>
                            <label className={classes.label} htmlFor="far-rule-prefix">File name starts with (prefix)</label>
                            <input id="far-rule-prefix" className={classes.input} value={form.prefix} onChange={(e) => set("prefix", e.target.value)} placeholder="e.g. inv" />
                        </div>
                        { showPrefixOp ? opSelect("prefixOp", "Join with") : null }
                    </div>
                    <div className={classes.row}>
                        <div className={classes.field+" "+classes.grow}>
                            <label className={classes.label} htmlFor="far-rule-suffix">File name ends with (suffix)</label>
                            <input id="far-rule-suffix" className={classes.input} value={form.suffix} onChange={(e) => set("suffix", e.target.value)} placeholder="e.g. _abc" />
                        </div>
                        { showSuffixOp ? opSelect("suffixOp", "Join with") : null }
                    </div>
                    <div className={classes.field}>
                        <label className={classes.label} htmlFor="far-rule-sendlinx-folder">Sendlinx folder (My Docs)</label>
                        <div className={classes.row} style={{flexWrap: "nowrap", alignItems: "stretch"}}>
                            <input id="far-rule-sendlinx-folder" className={classes.input} value={form.sendlinxFolder || ""} readOnly
                                placeholder="No folder selected" aria-describedby="far-rule-sendlinx-folder-help" onClick={() => setFolderPicker(true)} style={{cursor: "pointer"}} />
                            <button type="button" className={classes.cancel} onClick={() => setFolderPicker(true)} style={{whiteSpace: "nowrap"}}>
                                <FolderOpen style={{width: 18, height: 18}} aria-hidden="true" />{ hasSendlinxFolder ? "Change" : "Browse" }
                            </button>
                            { hasSendlinxFolder ? <button type="button" className={classes.cancel} onClick={() => set("sendlinxFolder", "")} aria-label="Clear Sendlinx folder">Clear</button> : null }
                        </div>
                        <p className={classes.help} id="far-rule-sendlinx-folder-help">Checked on the web portal when files are sent from My Docs. Includes subfolders. Uploaded files are not in a Sendlinx folder, so they don't match this condition.</p>
                    </div>
                    <div className={classes.field}>
                        <label className={classes.label} htmlFor="far-rule-directory">Local folder (Outlook plug-in)</label>
                        <input id="far-rule-directory" className={classes.input} value={form.directory} onChange={(e) => set("directory", e.target.value)} placeholder="e.g. C:\customers\ABC_Inc\invoices\" aria-describedby="far-rule-directory-help" />
                        <p className={classes.help} id="far-rule-directory-help">A folder on the sender's computer. Only the Outlook plug-in can check it; browsers don't share local folder paths, so the web portal skips it.</p>
                    </div>
                    <p className={classes.help} id="far-rule-conditions-help">
                        Conditions are evaluated left to right: prefix, then suffix, then folder (the Sendlinx folder on the web, the local folder in Outlook).
                        Suffix matches the end of the name before the extension (Inv-637<strong>_abc</strong>.pdf) or the full name (.pdf).
                    </p>
                    { hasDirectory && !hasPrefix && !hasSuffix && !hasSendlinxFolder ? <FarNotice tone="info">This rule only has a local folder condition, so the Outlook plug-in checks it and the web portal doesn't. Add a Sendlinx folder to cover files sent from My Docs.</FarNotice> : null }
                    { errors.conditions ? <p className={classes.error} role="alert">{errors.conditions}</p> : null }
                </section>

                <section className={classes.section} aria-labelledby="far-rule-recipients">
                    <h3 className={classes.sectionTitle} id="far-rule-recipients">Authorized recipients<span className={classes.required} aria-hidden="true">*</span></h3>
                    <FarEmailChips id="far-rule-emails" classes={classes} emails={form.emails} onChange={(emails) => set("emails", emails)}
                        invalid={!!errors.emails} describedBy="far-rule-emails-help" label="Authorized recipient email addresses" />
                    <p className={classes.help} id="far-rule-emails-help">Press Enter, Tab or comma after each address. You can paste a list.</p>
                    { errors.emails ? <p className={classes.error} role="alert">{errors.emails}</p> : null }
                </section>

                <section className={classes.section+" "+classes.exclusionSection} aria-labelledby="far-rule-exclusions" aria-describedby="far-rule-exclusions-help">
                    <h3 className={classes.sectionTitle} id="far-rule-exclusions">Recipient Exclusions (Blocked)</h3>
                    <p className={classes.help} id="far-rule-exclusions-help" style={{marginTop: 0}}>
                        When a file matches this rule, it can never be sent to these addresses or to anyone at these domains — even if they are also listed as authorized recipients.
                    </p>
                    {
                        canEditExclusions ?
                            <>
                                <div className={classes.field}>
                                    <label className={classes.label} htmlFor="far-rule-blocked-emails">Blocked email addresses</label>
                                    <FarEmailChips id="far-rule-blocked-emails" classes={classes} emails={blockedEmails} onChange={(list) => set("blockedEmails", list)}
                                        normalize={normalizeBlockedEmail} max={FAR_MAX_BLOCKED_ENTRIES} itemsLabel="blocked email addresses" chipClassName={classes.blockedChip}
                                        placeholder="e.g. ceo@competitor.com" describedBy="far-rule-blocked-emails-help" label="Blocked email addresses" />
                                    <p className={classes.help} id="far-rule-blocked-emails-help">Exact addresses, not case-sensitive. Press Enter, comma or a new line after each one.</p>
                                </div>
                                <div className={classes.field}>
                                    <label className={classes.label} htmlFor="far-rule-blocked-domains">Blocked domains</label>
                                    <FarEmailChips id="far-rule-blocked-domains" classes={classes} emails={blockedDomains} onChange={(list) => set("blockedDomains", list)}
                                        normalize={normalizeBlockedDomain} describeError={(rejected) => describeBlockedDomainError(rejected[0])}
                                        max={FAR_MAX_BLOCKED_ENTRIES} itemsLabel="blocked domains" chipClassName={classes.blockedChip}
                                        placeholder="e.g. competitor.com" describedBy="far-rule-blocked-domains-help" label="Blocked domains" />
                                    <p className={classes.help} id="far-rule-blocked-domains-help">
                                        Domain only, without "@" or http:// (competitor.com). Blocks everyone at exactly that domain; subdomains such as mail.competitor.com and look-alikes such as notcompetitor.com are not included.
                                    </p>
                                </div>
                                <fieldset className={classes.radioGroup}>
                                    <legend className={classes.legend}>When an excluded recipient is detected</legend>
                                    {
                                        [FAR_EXCLUSION.BLOCK, FAR_EXCLUSION.ADMIN, FAR_EXCLUSION.GLOBAL].map(action => (
                                            <label className={classes.radio} key={action}>
                                                <input type="radio" name="far-rule-exclusion-action" value={action} checked={form.exclusionAction === action}
                                                    onChange={() => set("exclusionAction", action)} />
                                                <span>
                                                    {FAR_EXCLUSION_LABEL[action]}{ action === FAR_EXCLUSION.BLOCK ? " (recommended)" : "" }
                                                    <small>{EXCLUSION_HELP[action]}</small>
                                                </span>
                                            </label>
                                        ))
                                    }
                                </fieldset>
                                {
                                    conflicts.length > 0 ?
                                        <FarNotice tone="warning">
                                            {conflicts.join(", ")} {conflicts.length === 1 ? "is" : "are"} also an authorized recipient. The exclusion takes precedence, so files matching this rule will not be sent to {conflicts.length === 1 ? "it" : "them"}.
                                        </FarNotice>
                                    : null
                                }
                                {
                                    hasExclusions ?
                                        <p className={classes.example} aria-live="polite">
                                            <strong>Example rule outcome:</strong> sending "{exampleFile}" to {exampleRecipient} is <strong>blocked</strong>
                                            {
                                                form.exclusionAction === FAR_EXCLUSION.ADMIN ? " unless an administrator authorizes it" :
                                                form.exclusionAction === FAR_EXCLUSION.GLOBAL ? " unless an exception is authorized under the FAR Settings" :
                                                " and cannot be overridden"
                                            }. Exclusions override authorized recipients.
                                        </p>
                                    : null
                                }
                            </>
                        :
                            <FarNotice tone="info">Only the rule's owner or an administrator can see and change its blocked recipients.</FarNotice>
                    }
                </section>

                <p className={classes.help} style={{margin: 0}}>Exception behavior for recipients that are not excluded uses the global FAR Settings.</p>
                <button type="submit" style={{display: "none"}} aria-hidden="true" tabIndex={-1} />
            </form>
            <div className={classes.footer}>
                <button type="button" className={classes.cancel} onClick={onClose} disabled={saving}>Cancel</button>
                <button type="button" className={classes.accent} onClick={save} disabled={saving}>
                    { saving ? <LoaderComponent color="white" saving={true} /> : (mode === "edit" ? "Save Changes" : "Save Rule") }
                </button>
            </div>
            {
                folderPicker ?
                    <MyDocsPickerModal
                        open={folderPicker}
                        mode="folder"
                        title="Choose a Sendlinx folder"
                        subtitle="Files sent from this My Docs folder or its subfolders will be checked by this rule."
                        initialPath={form.sendlinxFolder || undefined}
                        onClose={() => setFolderPicker(false)}
                        onSelect={(folderPath) => { set("sendlinxFolder", folderPath); setFolderPicker(false); }}
                    />
                : null
            }
        </Dialog>
    );
}

FarRuleModal.defaultProps = { rule: null, mode: "add" };
FarRuleModal.propTypes = {
    open: PropTypes.bool.isRequired,
    rule: PropTypes.object,
    mode: PropTypes.oneOf(["add", "edit", "duplicate"]),
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
};

export default FarRuleModal;
